import { useId, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Button } from "@onengine/component-library";
import { useDockHeight } from "./dockContext";
import { fitTextarea } from "./fitTextarea";
import styles from "./Assistant.module.scss";

const PANEL_SHARE = 0.4;
const FALLBACK_MAX_HEIGHT = 240;

interface AssistantComposerProps {
  isBusy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  placeholder?: string;
  hint?: string;
  /** Shown instead of the hint, and blocks sending, when set. */
  disabledReason?: string;
}

export function AssistantComposer({
  isBusy,
  onSend,
  onStop,
  placeholder = "Describe a form, or ask for a change",
  hint = "The assistant cannot save, publish or assign.",
  disabledReason,
}: AssistantComposerProps) {
  const inputId = useId();
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dockHeight = useDockHeight();
  const canSend = !isBusy && !disabledReason && text.trim().length > 0;

  useLayoutEffect(() => {
    if (!inputRef.current) return;
    const maxHeight = dockHeight ? dockHeight * PANEL_SHARE : FALLBACK_MAX_HEIGHT;
    fitTextarea(inputRef.current, maxHeight);
  }, [text, dockHeight]);

  const send = () => {
    if (!canSend) return;
    onSend(text.trim());
    setText("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
    if (event.key === "Escape" && isBusy) onStop();
  };

  return (
    <div className={styles.composer}>
      <label htmlFor={inputId} className={styles.composerLabel}>
        Message the assistant
      </label>
      <textarea
        id={inputId}
        ref={inputRef}
        className={styles.composerInput}
        rows={3}
        maxLength={4000}
        value={text}
        placeholder={placeholder}
        disabled={Boolean(disabledReason)}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <div className={styles.composerFooter}>
        <span className={styles.cardMeta}>{disabledReason ?? hint}</span>
        {isBusy ? (
          <Button variant="secondary" size="small" onClick={onStop}>
            Stop
          </Button>
        ) : (
          <Button variant="primary" size="small" onClick={send} isDisabled={!canSend}>
            Send
          </Button>
        )}
      </div>
    </div>
  );
}
